<template>
  <StepLayout
    :back="back"
    :next="next"
    :isEditRoute="true"
    helpscoutArticle="66619e601dd56877041fcc02"
    buttonDataEc="envelopes_sharing_page"
  >
    <template v-slot:title>
      {{ $t('ui.envelopes.create.steps.sharing.title', { title }) }}
    </template>

    <div class="flex flex-col max-w-screen-sm">
      <SharingSelector
        v-if="!isPendingSharing"
        :value="{ ...sharing }"
        @input="handleSharing"
        @itemAdded="itemAdded"
        @itemRemoved="itemRemoved"
        @permissionChanged="permissionChanged"
        :permittedActorsListTitle="
          $t('sharing.currentSharing', { resource: $t('functional.envelope') })
        "
      />
      <ValidationError
        :hasError="hasErrors('sharing')"
        :errorMessage="getError('sharing')"
      />
    </div>
  </StepLayout>
</template>

<script setup>
// @ts-check
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router/composables';

import { logAmplitudeEvent } from '@/utils/analytics';
import {
  useEnvelope,
  useEnvelopeSharing,
  useEnvelopesStepsNavigation,
  useUpdateEnvelopeSharing,
} from '@/composables/envelopes';
import {
  useErrors,
  useUpdateWithValidation,
  useValidation,
} from '@/composables/envelopes/validation';
import { SharingUpdate } from '@/store/modules/users/models/Sharing';

import StepLayout from '@/components/layouts/StepLayout';
import ValidationError from '@/components/library/error/ValidationError/ValidationError.vue';
import SharingSelector from '@/components/Sharing.vue';

/** @typedef {import('@/store/modules/users/models/Sharing').TSharingUpdate} TSharingUpdate */

const route = useRoute();

const { envelope } = useEnvelope(route.params.fingerprint);
const { sharing, isPendingSharing } = useEnvelopeSharing(route.params.fingerprint);
const { updateSharing } = useUpdateEnvelopeSharing(route.params.fingerprint);

const title = computed(() => envelope.value?.title || '');

/** @type {import('vue').Ref<TSharingUpdate>} */
const sharingUpdate = ref({
  groups: [],
  users: [],
  everyone_permission_id: null,
});

const {
  isValid, data, errors, validate,
} = useValidation(
  sharingUpdate,
  SharingUpdate,
);

const { hasErrors, getError } = useErrors(errors);

function handleSharing(value) {
  sharingUpdate.value = value;
  validate();
}

const update = useUpdateWithValidation(updateSharing, data, validate);

const { back, next } = useEnvelopesStepsNavigation(update, isValid);

function itemAdded(item) {
  logAmplitudeEvent('envelopes_sharing_page--added_actor_to_template_sharing', {
    type: item.type,
  });
}
function itemRemoved(item) {
  logAmplitudeEvent(
    'envelopes_sharing_page--removed_actor_from_template_sharing',
    { type: item.type },
  );
}
function permissionChanged(value) {
  logAmplitudeEvent('envelopes_sharing_page--permission_changed', {
    type: value.slug,
  });
}
</script>
